/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  TextField,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Switch,
  Menu,
} from "@mui/material";
import Button from '@mui/material/Button';
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MicIcon from "@mui/icons-material/Mic";
import { useSpeechRecognition } from '../../utils/speechRecognition';
import {
  handleSpeechInputChange,
  handleSpeechKeyDown,
  startListening as startListeningHelper,
  stopListening as stopListeningHelper
} from '../../utils/speechRecognitionHelpers';
import classes from "./CampaignBriefChat.module.scss";
import { fetchCampaignBriefChatData } from 'store';
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Link from '@mui/material/Link';
import campaignBriefApiService from "../../services/campaign_brief_api_service";
import { getFormattedTime } from "../../utils/date";
import { setChatHistories } from "../../store/slices/campaignBriefSlice";
import { resetChatRefreshFlag } from "../../store/slices/segmenterChatSlice";
import ReactMarkdown from 'react-markdown';
import { downloadMarkdownAsPDF } from '../../utils/downloadMarkdownAsPDF';
import { Box } from "@mui/system";
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { addDays, format } from 'date-fns';
import { NumericFormat } from "react-number-format";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { renderMarkdownToPDFFile } from "utils/generatePDF";
import { exportFileToS3 } from "store";

function sliceFromLastText(arr, targetText) {
  for (let i = arr.length - 1; i >= 0; i--) {
    const item = arr[i];
    if (!item || !Array.isArray(item.content)) continue;
    for (const c of item.content) {
      if (c && c.text === targetText) {
        return arr.slice(i);
      }
    }
  }
  return [];
}

const CampaignBriefChat = React.memo(
  ({ defaultQuery = "" } = {}) => {

    const [input, setInput] = useState("");
    // Speech-to-text states
    const [supported, setSupported] = useState(true);
    const [listening, setListening] = useState(false);
    const finalRef = useRef("");
    const recognitionRef = useRef(null);
    // Speech Recognition setup
    useSpeechRecognition({
      finalRef,
      setInput,
      setListening,
      setSupported,
      recognitionRef,
      lang: 'en-US',
      continuous: true,
      interimResults: true,
    });
    const startListening = () => startListeningHelper(recognitionRef, setListening, input, finalRef);
    const stopListening = () => stopListeningHelper(recognitionRef, setListening);
    const [isLoading, setIsLoading] = useState(false);
    //eslint-disable-next-line
    const [selectedChat, setSelectedChat] = useState(0);
    const messagesEndRef = useRef(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const showSnackbar = (message, severity = "success") => {
      setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = (event, reason) => {
      if (reason === 'clickaway') return;
      setSnackbar({ ...snackbar, open: false });
    };

    const [confluenceUrl, setconfluenceUrl] = useState(false);

    const [amount, setAmount] = React.useState('');
    const [currency, setCurrency] = React.useState('$');
    const [error, setError] = useState({ flag: false, message: '' });
    const [from, setFrom] = useState(null);
    const [to, setTo] = useState(null);
    const [checked, setChecked] = useState(false);

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);


    const dispatch = useDispatch();

    const reduxChatHistories = useSelector((state) => state.campaignBrief.chatHistories);
    const campaignBriefdata = useSelector((state) => state.campaignBrief.campaignBriefdata);
    const seg_conversation = useSelector((state) => state.segmenterChat.chatHistories);

    const initialChat = useMemo(() => [
      {
        role: "user",
        content: "Generate Campaign Brief based on the client conversation",
        timestamp: getFormattedTime(),
      },
    ], [defaultQuery]);

    const chatHistories = useMemo(() => (
      reduxChatHistories && reduxChatHistories.length > 0 ? reduxChatHistories : [initialChat]
    ), [reduxChatHistories, initialChat]);

    useEffect(() => {
      if (!chatHistories) return;
      const selectedItem = chatHistories[selectedChat]
      const lastElement = selectedItem && selectedItem[selectedItem.length - 1]
      if (messagesEndRef.current && lastElement?.role === 'user') {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      // eslint-disable-next-line
    }, [chatHistories, selectedChat]);

    const validate = () => {
      let validationError = { flag: false, message: '' };

      if (to === null && from === null) {
        validationError = { flag: false, message: '' };
      } else if (to === null && from !== null) {
        validationError = { flag: true, message: 'Please select the end date' };
      } else if (to !== null && from === null) {
        validationError = { flag: true, message: 'Please select the start date' };
      } else if (to && from && to < from) {
        validationError = { flag: true, message: 'Start Date should be less than End Date' };
      } else {
        validationError = { flag: false, message: '' };
      }

      setError(validationError);
      return !validationError.flag;
    }

    const handleSubmit = async () => {
      const isValid = validate();
      if (isValid) {
        generateCampaignConversation();
      }
    }

    const generateCampaignConversation = () => {
      const conversationHistory = [
        {
          "role": "user",
          "content": [
            {
              "text": "Generate Campaign Brief based on the client conversation"
            }
          ]
        }
      ]
      setIsLoading(true);
      dispatch(resetChatRefreshFlag());

      let updatedChats;

      if (campaignBriefdata) {
        const userMessage = {
          role: "user",
          content: "Generate Campaign Brief based on the client conversation",
          timestamp: getFormattedTime(),
        };
        // dispatch(setUserInput(input));
        updatedChats = [...chatHistories];
        updatedChats[selectedChat] = [
          ...updatedChats[selectedChat],
          userMessage,
        ];
        dispatch(setChatHistories(updatedChats));
      }
      fetchChatResponse(conversationHistory, updatedChats);
    };

    const pushToConfluence = async () => {
      if (!campaignBriefdata) return;
      const conversationHistory = [
        ...chatHistories[selectedChat].map((chat) => ({
          role: chat.role === "user" ? "user" : "assistant",
          content: [{ text: chat.content }],
        })),
      ];
      setIsLoading(true);

      const payload = {
        camp_brief_conversation: conversationHistory
      }
      const response = await campaignBriefApiService.pushCampaignToConfluence(payload);
      if (response) {
        setIsLoading(false);
        setconfluenceUrl(response.new_page_url);
        showSnackbar("Campaign pushed to Confluence successfully!");
      } else {
        console.error("Failed to push campaign to Confluence");
      }
    }

    const pushToS3 = async () => {

      if (!campaignBriefdata || !campaignBriefdata.conversation) return;
      const conversation = campaignBriefdata.conversation;
      const lastContent = conversation[conversation.length - 1]?.content?.[0]?.text || "";
      const pdfFile = await renderMarkdownToPDFFile(lastContent);
      const now = new Date();
      const file_name = `Campaign_Brief_Chat_${format(now, "yyyy-MM-dd_HH-mm-ss")}.pdf`;
      const conversationHistory = [
        ...chatHistories[selectedChat].map((chat) => ({
          role: chat.role === "user" ? "user" : "assistant",
          content: [{ text: chat.content }],
        })),
      ];

      setIsLoading(true);

      try {
        dispatch(exportFileToS3({ file: pdfFile, file_name: file_name, camp_brief_conversation: conversationHistory })).unwrap()
          .then((data) => {
            setIsLoading(false);
            showSnackbar("File Saved to S3 Successfully..")
            setError('');
          })
          .catch((err) => {
            setError(err.message);
          });
      } catch (error) {
        setError(error.message);
      } finally {
      }
    }

    const handleSendMessage = async () => {
      if (!input.trim() || !chatHistories) return;
      stopListening();

      const userMessage = {
        role: "user",
        content: input,
        timestamp: getFormattedTime(),
      };
      // dispatch(setUserInput(input));

      const updatedChats = [...chatHistories];
      updatedChats[selectedChat] = [
        ...updatedChats[selectedChat],
        userMessage,
      ];
      dispatch(setChatHistories(updatedChats));
      setIsLoading(true);

      const conversationHistory = [
        ...chatHistories[selectedChat].map((chat) => ({
          role: chat.role === "user" ? "user" : "assistant",
          content: [{ text: chat.content }],
        })),
        { role: "user", content: [{ text: input }] },
      ];

      const updatedConversationHistory = sliceFromLastText(conversationHistory, "Generate Campaign Brief based on the client conversation");

      await fetchChatResponse(updatedConversationHistory, updatedChats);
      setInput("");
      finalRef.current = '';
    };

    const fetchChatResponse = (conversationHistory, updatedChats = chatHistories) => {
      const payload = {
        conversation: conversationHistory,
        seg_conversation: seg_conversation,
        campaign_budget: amount ? `${currency}${amount}` : '',
        campaign_start_date: from ? format(from, 'do MMMM yyyy') : '',
        campaign_to_date: to ? format(to, 'do MMMM yyyy') : '',
        sub_segments: checked ? 'on' : 'off',
      }
      dispatch(fetchCampaignBriefChatData(payload))
        .unwrap()
        .then((data) => {

          const conversations = data?.conversation
          const lastContent = conversations[conversations.length - 1].content;

          const updatedChatContent = [...updatedChats];
          const updatedChatsAfter = [...updatedChatContent];

          updatedChatsAfter[selectedChat] = [
            ...updatedChatsAfter[selectedChat],
            { role: "assistant", content: lastContent[0].text, timestamp: getFormattedTime() },
          ];
          dispatch(setChatHistories(updatedChatsAfter));
          setIsLoading(false);
        })
        .catch((err) => {
          throw err;
        });
    };

    const handleKeyDown = (event) => handleSpeechKeyDown(event, stopListening, handleSendMessage);
    const handleInputChange = handleSpeechInputChange(setInput, finalRef);

    const handleTabChange = useCallback((event, newValue) => {
      setSelectedChat(newValue);
    }, []);

    const handleAddChat = useCallback(() => {
      if (!chatHistories) return;
      const updatedChats = [...chatHistories, []];
      dispatch(setChatHistories(updatedChats));
      setSelectedChat(chatHistories.length);
    }, [chatHistories, dispatch]);

    // eslint-disable-next-line
    const tabs = useMemo(
      () => (
        <Tabs
          value={selectedChat}
          onChange={handleTabChange}
          aria-label="chat tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className={classes.tabs}
        >
          {chatHistories && chatHistories.map((chat, index) => (
            <Tab key={index} label={`Chat ${index + 1}`} />
          ))}
          <IconButton
            className={classes.addChatButton}
            aria-label="add chat"
            onClick={handleAddChat}
          >
            <AddIcon />
          </IconButton>
        </Tabs>
      ),
      [selectedChat, chatHistories, handleAddChat, handleTabChange]
    );

    // PDF Download Functionality
    const handleDownloadPDF = async () => {
      if (!campaignBriefdata || !campaignBriefdata.conversation) return;
      const conversation = campaignBriefdata.conversation;
      const lastContent = conversation[conversation.length - 1]?.content?.[0]?.text || "";
      await downloadMarkdownAsPDF(lastContent, 'campaign_brief_chat.pdf');
    };

    const handleFromChange = (newDate) => {
      setFrom(newDate);
      if (to && newDate && to < newDate) {
        setTo(null);
      }
    };

    const handleSwitchChange = (event) => {
      setChecked(event.target.checked);
    };

    const handlePush = () => {
      if (typeof pushToConfluence === 'function') pushToConfluence();
      handleClose();
    };

    const handlePushToS3 = () => {
      if (typeof handlePushToS3 === 'function') pushToS3();
      handleClose();
    };

    const handleDownload = () => {
      if (typeof handleDownloadPDF === 'function') handleDownloadPDF();
      handleClose();
    };

    return (
      <>
        <div className={classes.chatContainer}>
          {/* <Box sx={{ borderBottom: 1, borderColor: "divider" }}>{tabs}</Box> */}
          <Box className={classes.topSection}>
            <Box className={classes.inputFields}>
              <Box sx={{ maxWidth: 520 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 400, mb: 1 }}>
                  Total Campaign Budget
                </Typography>
                <NumericFormat
                  className={classes.campaignBudget}
                  customInput={TextField}
                  sx={{ maxWidth: 200 }}
                  value={amount}
                  onValueChange={(values) => {
                    setAmount(values.formattedValue);
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  decimalScale={2}
                  allowNegative={false}
                  inputMode="decimal"
                  size="medium"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <FormControl variant="standard" sx={{ minWidth: 40 }}>
                          <Select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            size="medium"
                            disableUnderline
                          >
                            <MenuItem value="$">USD</MenuItem>
                            <MenuItem value="€">EUR</MenuItem>
                            <MenuItem value="£">GBP</MenuItem>
                            <MenuItem value="₹">INR</MenuItem>
                          </Select>
                        </FormControl>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ maxWidth: 520 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 400, mb: 1 }}>
                  Campaign Duration
                </Typography>
                <Stack direction="row" spacing={2} className={classes.dateStack}>
                  <div className={classes.datePicker}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={from}
                        className={classes.fromDate}
                        sx={{ maxWidth: 160 }}
                        onChange={handleFromChange}
                        label="From"
                        format="yyyy-MM-dd"
                        maxDate={to || undefined}
                        minDate={new Date()}
                      />
                      <DatePicker
                        value={to}
                        label="To"
                        className={classes.toDate}
                        sx={{ maxWidth: 160 }}
                        onChange={(newDate) => setTo(newDate)}
                        format="yyyy-MM-dd"
                        minDate={from ? addDays(from, 1) : addDays(new Date(), 1)}
                      />
                    </LocalizationProvider>
                  </div>
                </Stack>
              </Box>

              <Box sx={{ maxWidth: 520 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 400, mb: 1 }}>
                  Create Sub Segment
                </Typography>
                <Switch
                  checked={checked}
                  onChange={handleSwitchChange}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </Box>
            </Box>
            <Box className={classes.generateButton}>
              {true && <Box className={classes.converationButton}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  sx={{ minWidth: 56, marginRight: 1 }}
                  startIcon={<AutoAwesomeIcon />}
                >Generate</Button>
              </Box>}
            </Box>
          </Box>
          <Box>{error.flag && <MuiAlert className={classes.muiAlert} severity="error">
            {error.message}
          </MuiAlert>}</Box>

          <div className={classes.chatBox} id="campaign-brief-chat-box">
            {chatHistories && chatHistories[selectedChat]?.map((chat, index) => (
              <div
                key={index}
                className={
                  chat.role === "user" ? classes.userMessage : classes.apiMessage
                }
              >
                <div className={classes.chatBubble}>
                  <ReactMarkdown >{chat?.content}</ReactMarkdown>
                </div>
                <div className={classes.timestamp}>{chat.timestamp}</div>
              </div>
            ))}

            {isLoading && (
              <div className={classes.typingIndicator}>
                <CircularProgress size={20} />
              </div>
            )}
            <div ref={messagesEndRef} />

            {/* <Box className={classes.buttonSection}>
              <Box className={classes.utilityButtons}>
                {campaignBriefdata && <Button
                  variant="contained"
                  onClick={pushToConfluence}
                  disabled={confluenceUrl || isLoading}
                  sx={{ minWidth: 56, marginRight: 1 }}
                >Push To Confluence</Button>
                }

                {campaignBriefdata && <Button
                  variant="outlined"
                  onClick={handleDownloadPDF}
                  sx={{ minWidth: 56, marginLeft: 1 }}
                  disabled={isLoading}
                >Download as PDF</Button>
                }
              </Box>
            </Box> */}

            <Box className={classes.buttonSection}>
              <Box className={classes.utilityButtons}>
                {campaignBriefdata && (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleOpen}
                      disabled={isLoading}
                      sx={{ minWidth: 56 }}
                      endIcon={<KeyboardArrowDownIcon />}
                    >
                      Export
                    </Button>

                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={handlePush} disabled={confluenceUrl || isLoading}>
                        Push To Confluence
                      </MenuItem>
                      <MenuItem onClick={handlePushToS3} disabled={isLoading}>
                        Push To S3
                      </MenuItem>
                      <MenuItem onClick={handleDownload} disabled={isLoading}>
                        Download as PDF
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Box>

            {confluenceUrl && <Link href={confluenceUrl} underline="none" target="_blank" rel="noopener noreferrer">
              {'  Link To Confluence Page'}
            </Link>}

          </div>
          <div className={classes.inputContainer}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className={classes.textInput}
              autoComplete="off"
              placeholder="Type your message..."
              disabled={isLoading}
              inputRef={el => {
                if (el && listening === true) {
                  el.selectionStart = el.value.length;
                  el.selectionEnd = el.value.length;
                  el.focus();
                  const len = el.value.length;
                  el.setSelectionRange(len, len);
                  el.scrollLeft = el.scrollWidth;
                }
              }}
            />
            {/* Speech to Text Buttons */}
            {supported ? (
              <>
                <IconButton
                  onClick={listening ? stopListening : startListening}
                  color={listening ? "secondary" : "primary"}
                  aria-label="speech to text"
                  className={`${classes.sendButton} ${listening ? classes.micListening : ""}`}
                  disabled={isLoading}
                >
                  <MicIcon
                    style={
                      listening
                        ? {
                          animation: "pulse 1s infinite",
                          color: "#d32f2f",
                          filter: "drop-shadow(0 0 6px #d32f2f)",
                        }
                        : {}
                    }
                  />
                </IconButton>
              </>
            ) : (
              <span style={{ color: "red", marginRight: 8, fontSize: 12 }}>
                Speech API not supported
              </span>
            )}
            <IconButton
              onClick={handleSendMessage}
              color="primary"
              aria-label="send message"
              className={classes.sendButton}
              disabled={isLoading}
            >
              <ArrowUpwardIcon />
            </IconButton>
            {/* Add keyframes for pulse animation */}
            <style>
              {`
                @keyframes pulse {
                  0% { transform: scale(1); }
                  50% { transform: scale(1.2); }
                  100% { transform: scale(1); }
                }
              `}
            </style>
          </div>
        </div>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </>
    );
  }
);

export default CampaignBriefChat; 